import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle, Clock, Trophy, Loader2, Send, ArrowRight, Timer, MessageSquare, ThumbsUp, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sound effect utilities using Web Audio API
const audioCtxRef = { current: null };
function getAudioCtx() {
  if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtxRef.current;
}

function playBeep(freq = 800, duration = 0.1, volume = 0.3) {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    gain.gain.value = volume;
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    osc.stop(ctx.currentTime + duration);
  } catch (e) { /* silent fail */ }
}

function playCorrectSound() {
  playBeep(523, 0.1, 0.3);
  setTimeout(() => playBeep(659, 0.1, 0.3), 100);
  setTimeout(() => playBeep(784, 0.15, 0.3), 200);
}

function playWrongSound() {
  playBeep(200, 0.2, 0.2);
  setTimeout(() => playBeep(150, 0.3, 0.2), 200);
}

function playTickSound() {
  playBeep(1000, 0.05, 0.15);
}

const OPTION_COLORS = ['#E53E6B', '#FF9F43', '#00D2D3', '#5F27CD', '#10AC84', '#EE5A24'];

export default function QuizParticipant({ quizId, participantId, participantName }) {
  const [status, setStatus] = useState(null);
  const [answered, setAnswered] = useState(new Set());
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [score, setScore] = useState(0);
  const [selfPacedQuestions, setSelfPacedQuestions] = useState([]);
  const [selfPacedIndex, setSelfPacedIndex] = useState(0);
  const [wordCloudInput, setWordCloudInput] = useState('');
  const [rankingOrder, setRankingOrder] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [shakeWrong, setShakeWrong] = useState(false);
  const [lastPoints, setLastPoints] = useState(0);
  const [lastIsFirst, setLastIsFirst] = useState(false);
  const [showQAPanel, setShowQAPanel] = useState(false);
  const [qaText, setQaText] = useState('');
  const [qaQuestions, setQaQuestions] = useState([]);
  const [qaSubmitting, setQaSubmitting] = useState(false);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const lastTickRef = useRef(null);

  useEffect(() => {
    pollStatus();
    pollRef.current = setInterval(pollStatus, 2500);
    return () => clearInterval(pollRef.current);
  }, [quizId]);

  // Reset feedback state when admin advances to next question
  useEffect(() => {
    setLastResult(null);
    setShowConfetti(false);
    setShakeWrong(false);
    setLastPoints(0);
    setLastIsFirst(false);
  }, [status?.current_question_index]);

  // Timer countdown for participants
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    const timeLimit = status?.current_question?.time_limit || 0;
    const startedAt = status?.question_started_at;
    
    if (!timeLimit || !startedAt || status?.status !== 'live') {
      setTimeLeft(null);
      return;
    }
    
    const calcTimeLeft = () => {
      const elapsed = (Date.now() - new Date(startedAt).getTime()) / 1000;
      const remaining = Math.max(0, Math.ceil(timeLimit - elapsed));
      setTimeLeft(remaining);
      // Play tick sound at ≤5 seconds
      if (remaining > 0 && remaining <= 5 && remaining !== lastTickRef.current) {
        lastTickRef.current = remaining;
        playTickSound();
      }
      if (remaining <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
        playWrongSound(); // Time's up buzzer
      }
    };
    
    calcTimeLeft();
    timerRef.current = setInterval(calcTimeLeft, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [status?.current_question_index, status?.question_started_at]);

  const pollStatus = async () => {
    try {
      const res = await axios.get(`${API}/quiz/${quizId}/status`);
      setStatus(res.data);
      
      // Load self-paced questions if applicable
      if (res.data.mode === 'self_paced' && res.data.status === 'live' && selfPacedQuestions.length === 0) {
        const spRes = await axios.get(`${API}/quiz/${quizId}/self-paced`);
        setSelfPacedQuestions(spRes.data.questions);
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  };

  const submitAnswer = async (answer) => {
    const question = status?.mode === 'self_paced' 
      ? selfPacedQuestions[selfPacedIndex] 
      : status?.current_question;
    
    if (!question) return;
    if (answered.has(question.id)) return;
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quiz/${quizId}/answer`, {
        participant_id: participantId,
        question_id: question.id,
        answer: answer,
      });
      setAnswered(prev => new Set([...prev, question.id]));
      
      // For open_ended and word_cloud, don't show correct/incorrect
      const isJudgedType = question.type === 'multiple_choice' || question.type === 'true_false' || question.type === 'ranking';
      
      if (isJudgedType) {
        setLastResult(res.data.is_correct);
        setLastPoints(res.data.points || 0);
        setLastIsFirst(res.data.is_first || false);
        if (res.data.is_correct) {
          setScore(s => s + (res.data.points || 0));
          playCorrectSound();
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 2500);
        } else {
          playWrongSound();
          setShakeWrong(true);
          setTimeout(() => setShakeWrong(false), 600);
        }
      } else {
        // Open-ended / word cloud — just acknowledge submission
        setLastResult(null);
        setLastPoints(0);
        setLastIsFirst(false);
      }
      
      setSelectedAnswer(null);
      setWordCloudInput('');
      setRankingOrder([]);
      
      // Auto-advance in self-paced mode after 1.5s
      if (status?.mode === 'self_paced') {
        setTimeout(() => {
          if (selfPacedIndex < selfPacedQuestions.length - 1) {
            setSelfPacedIndex(i => i + 1);
            setLastResult(null);
          }
        }, 1500);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRankingMove = (fromIdx, toIdx) => {
    const newOrder = [...rankingOrder];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    setRankingOrder(newOrder);
  };

  const loadQAQuestions = async () => {
    try {
      const res = await axios.get(`${API}/quiz/${quizId}/qa`);
      setQaQuestions(res.data.questions || []);
    } catch (err) { console.error(err); }
  };

  const submitQA = async () => {
    if (!qaText.trim()) return;
    setQaSubmitting(true);
    try {
      await axios.post(`${API}/quiz/${quizId}/qa/submit`, { text: qaText, name: participantName });
      setQaText('');
      loadQAQuestions();
    } catch (err) { alert('Failed to submit question'); }
    finally { setQaSubmitting(false); }
  };

  const upvoteQA = async (qaId) => {
    try {
      await axios.post(`${API}/quiz/${quizId}/qa/${qaId}/upvote`, { voter_id: participantId });
      loadQAQuestions();
    } catch (err) { console.error(err); }
  };

  // Waiting for quiz to start
  if (!status || status.status === 'draft') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} data-testid="quiz-waiting">
        <div className="text-center max-w-sm">
          <Loader2 className="w-8 h-8 animate-spin text-white mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Waiting for host...</h2>
          <p className="text-white/70 text-sm">The quiz will start shortly. Hang tight, {participantName}!</p>
          <div className="mt-4 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl inline-block border border-white/20">
            <span className="text-xs text-white/60">Your name:</span>
            <p className="font-bold text-white">{participantName}</p>
          </div>
        </div>
      </div>
    );
  }

  // Quiz ended
  if (status.status === 'ended') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} data-testid="quiz-ended">
        <div className="text-center max-w-sm">
          <Trophy className="w-14 h-14 text-amber-300 mx-auto mb-4 drop-shadow-lg" />
          <h2 className="text-3xl font-black text-white mb-2">Quiz Complete!</h2>
          <p className="text-white/70 mb-4">Great job, {participantName}!</p>
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <p className="text-5xl font-black text-white">{score}</p>
            <p className="text-sm text-white/60">points scored</p>
          </div>
        </div>
      </div>
    );
  }

  // Determine current question based on mode
  const currentQuestion = status.mode === 'self_paced' 
    ? selfPacedQuestions[selfPacedIndex] 
    : status.current_question;

  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <Clock className="w-8 h-8 text-white/60 mx-auto mb-3 animate-pulse" />
          <p className="text-white/70">Waiting for next question...</p>
        </div>
      </div>
    );
  }

  const hasAnswered = answered.has(currentQuestion.id);
  const questionIndex = status.mode === 'self_paced' ? selfPacedIndex : (status.current_question_index || 0);
  const totalQuestions = status.mode === 'self_paced' ? selfPacedQuestions.length : (status.total_questions || 0);

  return (
    <div className={`min-h-screen flex flex-col ${shakeWrong ? 'animate-shake' : ''}`} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} data-testid="quiz-participant">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" data-testid="confetti">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-3 h-3 rounded-sm animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${1.5 + Math.random() * 1}s`,
                backgroundColor: ['#E53E6B', '#FF9F43', '#00D2D3', '#5F27CD', '#10AC84', '#EE5A24'][i % 6],
                transform: `rotate(${Math.random() * 360}deg)`,
              }}
            />
          ))}
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti {
          animation: confetti-fall 2s ease-out forwards;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 50%, 90% { transform: translateX(-4px); }
          30%, 70% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>

      {/* Header */}
      <div className="bg-black/20 backdrop-blur-sm text-white px-4 py-3 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold">{participantName}</span>
          <span className="text-white/40 text-xs">| Q{questionIndex + 1}/{totalQuestions}</span>
        </div>
        <div className="flex items-center gap-2 text-sm bg-white/10 px-3 py-1 rounded-full">
          <Trophy className="w-4 h-4 text-amber-300" />
          <span className="font-black">{score}</span>
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 p-4 flex flex-col">
        <div className="bg-white/15 backdrop-blur-md rounded-2xl shadow-lg p-5 mb-4 border border-white/20">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-white/50 uppercase tracking-wider font-bold">
              {currentQuestion.type.replace('_', ' ')}
            </p>
            {timeLeft !== null && (
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${timeLeft <= 5 ? 'bg-rose-500/80 text-white animate-pulse' : timeLeft <= 10 ? 'bg-amber-500/80 text-white' : 'bg-white/15 text-white'}`} data-testid="participant-timer">
                <Timer className="w-4 h-4" />
                {timeLeft > 0 ? `${timeLeft}s` : "Time's up!"}
              </div>
            )}
          </div>
          <h3 className="text-lg font-black text-white leading-relaxed">{currentQuestion.text}</h3>
          {currentQuestion.image_url && (
            <img src={currentQuestion.image_url} alt="" className="mt-3 rounded-xl max-h-48 object-contain mx-auto" />
          )}
        </div>

        {/* Answer Feedback (for graded types only) */}
        {hasAnswered && lastResult !== null && (
          <div className={`rounded-2xl p-4 mb-4 text-center font-bold transition-all duration-300 ${lastResult ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'}`} data-testid="answer-feedback">
            {lastResult ? (
              <div>
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5 inline" />
                  <span>Correct!</span>
                  {lastIsFirst && (
                    <span className="px-2 py-0.5 bg-amber-400/20 text-amber-300 text-xs rounded-full font-black animate-pulse border border-amber-400/30" data-testid="first-badge">
                      FIRST!
                    </span>
                  )}
                </div>
                {lastPoints > 0 && (
                  <p className="text-2xl font-black mt-1" data-testid="points-earned">
                    +{lastPoints} pts
                    {lastIsFirst && <span className="text-xs ml-1 text-amber-300">(+200 speed bonus)</span>}
                  </p>
                )}
              </div>
            ) : (
              <><span className="inline-block mr-2">&#10005;</span>Not quite — 0 pts</>
            )}
          </div>
        )}

        {/* Live poll results (shown after answering if show_results_live) */}
        {hasAnswered && currentQuestion.show_results_live && status?.answer_distribution && (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-sm p-4 mb-4 border border-white/15" data-testid="live-poll-results">
            {Object.entries(status.answer_distribution.answers || {}).map(([ans, count], i) => {
              const total = status.answer_distribution.total_responses || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={ans} className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-bold text-white">{ans}</span>
                    <span className="text-white/50">{pct}% ({count})</span>
                  </div>
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: OPTION_COLORS[i % OPTION_COLORS.length] }} />
                  </div>
                </div>
              );
            })}
            {status.answer_distribution.average_rating !== undefined && (
              <div className="text-center mt-3">
                <span className="text-2xl font-black text-amber-300">{status.answer_distribution.average_rating}</span>
                <span className="text-sm text-white/50"> / 5 average</span>
              </div>
            )}
          </div>
        )}

        {/* Answer Input based on type */}
        {!hasAnswered && timeLeft !== 0 && (
          <div className="flex-1">
            {/* Multiple Choice / True-False / Poll (single) */}
            {['multiple_choice', 'true_false', 'poll'].includes(currentQuestion.type) && (
              <div className="space-y-3">
                {(currentQuestion.options || []).map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => submitAnswer(opt)}
                    disabled={submitting}
                    className="w-full p-4 rounded-2xl text-left font-bold transition-all text-white hover:scale-[1.02] hover:brightness-110 active:scale-[0.98]"
                    style={{
                      backgroundColor: OPTION_COLORS[i % OPTION_COLORS.length],
                      boxShadow: `0 4px 15px ${OPTION_COLORS[i % OPTION_COLORS.length]}50`,
                      opacity: submitting ? 0.6 : 1,
                    }}
                    data-testid={`answer-option-${i}`}
                  >
                    <span className="inline-flex w-8 h-8 rounded-xl bg-white/20 items-center justify-center text-sm font-black mr-3">
                      {String.fromCharCode(65 + i)}
                    </span>
                    {opt}
                  </button>
                ))}
              </div>
            )}

            {/* Poll Multi-select */}
            {currentQuestion.type === 'poll_multi' && (
              <div className="space-y-3">
                {(currentQuestion.options || []).map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setSelectedAnswer(prev => {
                        const arr = Array.isArray(prev) ? [...prev] : [];
                        const idx = arr.indexOf(opt);
                        if (idx >= 0) arr.splice(idx, 1); else arr.push(opt);
                        return arr;
                      });
                    }}
                    disabled={submitting}
                    className={`w-full p-4 rounded-2xl text-left font-bold transition-all text-white ${
                      Array.isArray(selectedAnswer) && selectedAnswer.includes(opt) ? 'ring-4 ring-white/50 scale-[1.02]' : 'opacity-80 hover:opacity-100'
                    }`}
                    style={{ backgroundColor: OPTION_COLORS[i % OPTION_COLORS.length] }}
                    data-testid={`answer-option-${i}`}
                  >
                    <span className={`inline-flex w-6 h-6 rounded-lg border-2 items-center justify-center text-xs mr-3 ${
                      Array.isArray(selectedAnswer) && selectedAnswer.includes(opt) ? 'bg-white border-white text-violet-700 font-black' : 'border-white/40'
                    }`}>
                      {Array.isArray(selectedAnswer) && selectedAnswer.includes(opt) && '✓'}
                    </span>
                    {opt}
                  </button>
                ))}
                <button
                  onClick={() => submitAnswer(selectedAnswer || [])}
                  disabled={submitting || !Array.isArray(selectedAnswer) || selectedAnswer.length === 0}
                  className="w-full py-3 bg-white/20 backdrop-blur-sm text-white rounded-2xl font-bold flex items-center justify-center gap-2 disabled:opacity-30 hover:bg-white/30 transition-all border border-white/20"
                  data-testid="submit-poll-multi"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit ({Array.isArray(selectedAnswer) ? selectedAnswer.length : 0} selected)
                </button>
              </div>
            )}

            {/* Rating Scale (1-5 stars) */}
            {currentQuestion.type === 'rating' && (
              <div className="space-y-4">
                <div className="flex justify-center gap-3">
                  {[1, 2, 3, 4, 5].map(val => (
                    <button
                      key={val}
                      onClick={() => setSelectedAnswer(val)}
                      disabled={submitting}
                      className={`w-14 h-14 rounded-2xl text-2xl transition-all ${selectedAnswer === val ? 'bg-amber-400 text-white scale-110 shadow-[0_0_20px_rgba(251,191,36,0.5)]' : 'bg-white/15 border-2 border-white/20 text-white/70 hover:border-amber-400/50'}`}
                      data-testid={`rating-${val}`}
                    >
                      {'★'}
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-white/50">{selectedAnswer ? `${selectedAnswer} / 5` : 'Tap to rate'}</p>
                <button
                  onClick={() => submitAnswer(selectedAnswer)}
                  disabled={submitting || !selectedAnswer}
                  className="w-full py-3 bg-white/20 backdrop-blur-sm text-white rounded-2xl font-bold flex items-center justify-center gap-2 disabled:opacity-30 hover:bg-white/30 transition-all border border-white/20"
                  data-testid="submit-rating"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit Rating
                </button>
              </div>
            )}

            {/* Emoji Reaction */}
            {currentQuestion.type === 'emoji_reaction' && (
              <div className="space-y-3">
                <div className="flex flex-wrap justify-center gap-4">
                  {(currentQuestion.options || ['👍', '❤️', '😂', '😮', '🙏', '🔥']).map((emoji, i) => (
                    <button
                      key={i}
                      onClick={() => submitAnswer(emoji)}
                      disabled={submitting}
                      className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm border-2 border-white/20 text-3xl hover:scale-125 hover:bg-white/25 transition-all active:scale-90"
                      data-testid={`emoji-option-${i}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Open Ended / Word Cloud */}
            {(currentQuestion.type === 'open_ended' || currentQuestion.type === 'word_cloud') && (
              <div className="space-y-3">
                <textarea
                  value={wordCloudInput}
                  onChange={e => setWordCloudInput(e.target.value)}
                  placeholder={currentQuestion.type === 'word_cloud' ? 'Enter a word or short phrase...' : 'Type your answer...'}
                  rows={currentQuestion.type === 'word_cloud' ? 2 : 4}
                  className="w-full px-4 py-3 bg-white/15 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder:text-white/30 focus:ring-2 focus:ring-white/40 focus:border-transparent resize-none"
                  data-testid="open-answer-input"
                />
                <button
                  onClick={() => submitAnswer(wordCloudInput)}
                  disabled={submitting || !wordCloudInput.trim()}
                  className="w-full py-3 bg-white/20 backdrop-blur-sm text-white rounded-2xl font-bold flex items-center justify-center gap-2 disabled:opacity-30 hover:bg-white/30 transition-all border border-white/20"
                  data-testid="submit-open-answer"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit
                </button>
              </div>
            )}

            {/* Ranking */}
            {currentQuestion.type === 'ranking' && (
              <div className="space-y-3">
                <p className="text-sm text-white/50 mb-2">Tap to move up:</p>
                {(rankingOrder.length ? rankingOrder : currentQuestion.options || []).map((opt, i) => (
                  <button
                    key={`${opt}-${i}`}
                    onClick={() => { if (i > 0) handleRankingMove(i, i - 1); }}
                    className="w-full p-3 rounded-2xl text-left font-bold text-white flex items-center gap-3 hover:brightness-110 transition-all"
                    style={{ backgroundColor: OPTION_COLORS[i % OPTION_COLORS.length] }}
                  >
                    <span className="w-7 h-7 rounded-lg bg-white/20 text-white text-xs flex items-center justify-center font-black">{i + 1}</span>
                    {opt}
                  </button>
                ))}
                <button
                  onClick={() => submitAnswer(rankingOrder.length ? rankingOrder : currentQuestion.options)}
                  disabled={submitting}
                  className="w-full py-3 bg-white/20 backdrop-blur-sm text-white rounded-2xl font-bold flex items-center justify-center gap-2 disabled:opacity-30 hover:bg-white/30 transition-all border border-white/20"
                  data-testid="submit-ranking"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit Ranking
                </button>
              </div>
            )}
          </div>
        )}

        {hasAnswered && lastResult === null && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-2 drop-shadow-lg" />
              <p className="text-white font-bold text-lg">Answer submitted!</p>
              <p className="text-sm text-white/50">Waiting for next question...</p>
            </div>
          </div>
        )}

        {!hasAnswered && timeLeft === 0 && (
          <div className="flex-1 flex items-center justify-center" data-testid="time-expired">
            <div className="text-center">
              <Clock className="w-12 h-12 text-rose-300 mx-auto mb-2 animate-pulse" />
              <p className="text-rose-300 font-black text-xl">{"Time's up!"}</p>
              <p className="text-sm text-white/40">Waiting for next question...</p>
            </div>
          </div>
        )}
      </div>

      {/* Q&A Floating Button */}
      <button
        onClick={() => { setShowQAPanel(!showQAPanel); if (!showQAPanel) loadQAQuestions(); }}
        className="fixed bottom-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm text-white rounded-full shadow-lg flex items-center justify-center hover:bg-white/30 transition-all border border-white/20 z-40"
        data-testid="qa-fab"
      >
        <MessageSquare className="w-5 h-5" />
      </button>

      {/* Q&A Panel */}
      {showQAPanel && (
        <div className="fixed bottom-20 right-4 w-80 bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 z-40 max-h-[70vh] flex flex-col" data-testid="qa-panel">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <h4 className="font-bold text-white">Ask a Question</h4>
            <button onClick={() => setShowQAPanel(false)} className="p-1 hover:bg-white/10 rounded text-white/50"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {qaQuestions.map(q => (
              <div key={q.id} className={`p-3 rounded-xl ${q.pinned ? 'bg-sky-500/20 border border-sky-500/30' : 'bg-white/5 border border-white/5'}`}>
                <p className="text-sm text-white">{q.text}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-white/40">{q.submitted_by}</span>
                  <button onClick={() => upvoteQA(q.id)} className="flex items-center gap-1 text-xs text-white/50 hover:text-amber-300">
                    <ThumbsUp className="w-3 h-3" /> {q.votes}
                  </button>
                </div>
              </div>
            ))}
            {qaQuestions.length === 0 && <p className="text-sm text-white/30 text-center py-4">No questions yet</p>}
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              type="text"
              value={qaText}
              onChange={e => setQaText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitQA(); }}
              placeholder="Type your question..."
              className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-sm text-white placeholder:text-white/30 focus:ring-2 focus:ring-white/30"
              data-testid="qa-input"
            />
            <button onClick={submitQA} disabled={qaSubmitting || !qaText.trim()} className="px-3 py-2 bg-white/20 text-white rounded-xl disabled:opacity-30 hover:bg-white/30">
              {qaSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
